import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

interface TileMenuProps {
  /** Accessible name of the trigger, e.g. "More for Spring launch". */
  label: string;
  /** The actions. Real controls — they keep their own busy and message state. */
  children: ReactNode;
}

/**
 * The ⋯ overflow on a post tile: a disclosure, not an ARIA menu. The panel
 * holds ordinary buttons (Download ZIP), so Tab reaches them in order and no
 * roving focus is needed. Escape closes it and puts focus back on the trigger;
 * a press anywhere outside closes it.
 *
 * It stays mounted while open, so a download started from it keeps reporting
 * ("Preparing", "Still rendering") until the user closes it.
 */
export function TileMenu({ label, children }: TileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="tile-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        className="tile-menu__trigger"
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls={panelId}
        title="More"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      <div className="tile-menu__panel" id={panelId} hidden={!isOpen}>
        {children}
      </div>
    </div>
  );
}
